import { Calendar, ExternalLink, Eye, Hash, Mail, MapPin, Pencil, Phone, Plus, ReceiptText, Truck, User, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { date, dayOnly, money, phone, trackingUrl } from "../../format";
import { totals } from "../../pricing";
import type { AddressFields, TicketDetail } from "../../types";
import { Card, statusTone } from "./Card";
import { Modal } from "./Modal";
import { useTicketContext } from "./useTicket";
import { confirmBox } from "../../confirm";

export function SummaryCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [editLoc, setEditLoc] = useState(false);
  const [editDue, setEditDue] = useState(false);
  const paid = detail.payments.filter((p) => p.succeeded).reduce((s, p) => s + (p.kind === "refund" ? -1 : 1) * Number(p.amount), 0);
  const taxed = !!detail.customer?.chargeSalesTax && (t.state ?? "").trim().toUpperCase() === "WI";
  const sums = lookups ? totals(detail.items, Number(t.insuranceAmount ?? 0), taxed, lookups.pricing) : null;
  const total = sums?.total ?? 0;
  const due = Math.max(0, total - paid);
  // Profit = the ticket's revenue (services and insurance, without sales tax) minus all its expenses;
  // markup = revenue ÷ profit, as Joe defined it (owner, 2026-11-03).
  const revenue = sums?.subtotal ?? 0;
  const expenses = detail.expenses.reduce((s, e) => s + Number(e.amount), 0);
  const profit = Math.round((revenue - expenses) * 100) / 100;
  const markup = profit > 0 ? revenue / profit : null;
  const locations = lookups?.locations.filter((l) => l.isActive || l.id === t.locationId) ?? [];
  return (
    <Card title="Ticket Summary" icon={ReceiptText}>
      <div className="sum-top">
        <div><div className="lbl">Ticket Number</div><div className="big-no">{t.ticketNo}</div></div>
        <span className={`pill ${statusTone(t.status, t.statusIsClosed)}`}>{t.status}</span>
      </div>
      <dl className="sum">
        <dt><Calendar size={15} />Due Date</dt>
        <dd>
          {editDue ? (
            <input type="date" autoFocus defaultValue={t.dueDate?.slice(0, 10) ?? ""} disabled={saving}
              onBlur={() => setEditDue(false)}
              onChange={(e) => { void save("PATCH", "", { dueDate: e.target.value || null, expectedUpdatedAt: t.updatedAt }); setEditDue(false); }} />
          ) : (
            <button type="button" className="inline-edit" onClick={() => setEditDue(true)} title="Change due date">
              {t.dueDate ? dayOnly(t.dueDate) : "TBD"}
            </button>
          )}
        </dd>
        <dt><Calendar size={15} />Created Date</dt><dd>{date(t.createdAt)}</dd>
        <dt><MapPin size={15} />Location</dt>
        <dd className="with-edit">
          {editLoc ? (
            <select autoFocus value={t.locationId ?? ""} disabled={saving} onBlur={() => setEditLoc(false)}
              onChange={(e) => { void save("PATCH", "", { locationId: e.target.value || null, expectedUpdatedAt: t.updatedAt }); setEditLoc(false); }}>
              <option value="">—</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.kind === "watchmaker" ? " (watchmaker)" : ""}</option>)}
            </select>
          ) : (<>
            <span>{t.location ?? "—"}</span>
            <button type="button" className="icon-link" aria-label="Change location" onClick={() => setEditLoc(true)}><Pencil size={15} /></button>
          </>)}
        </dd>
        <dt><Users size={15} />Watchmakers</dt>
        <dd><Watchmakers /></dd>
        {t.leadSourcePath && (<>
          <dt><ExternalLink size={15} />Source</dt>
          <dd className="with-edit">
            <a className="ellipsis" href={`https://${t.leadSourcePath}`} target="_blank" rel="noreferrer" title={t.leadSourcePath}>https://{t.leadSourcePath}</a>
            <a className="icon-link" href={`https://${t.leadSourcePath}`} target="_blank" rel="noreferrer" aria-label="Open source page"><ExternalLink size={15} /></a>
          </dd>
        </>)}
        <dt className="strong">Paid / Due</dt>
        <dd><span className="paid">{money(paid)}</span>{due > 0.005 && <span className="due"> · {money(due)} due</span>}</dd>
        <dt className="strong">Profit / Markup</dt>
        <dd title={`Revenue ${money(revenue)} (no sales tax) − expenses ${money(expenses)}`}>
          <span className={profit < 0 ? "due" : "paid"}>{money(profit)}</span>
          <span className="hint"> · {markup == null ? "—" : `${markup.toFixed(1)}×`}</span></dd>
      </dl>
    </Card>
  );
}

/** Customer Information with the ticket's address to its right, in one card (owner, 2026-11-03). */
export function CustomerCard() {
  const { detail, save, saving } = useTicketContext();
  const c = detail.customer;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", allowEmail: true, allowText: false });
  if (!c) return <Card title="Customer Information" icon={User}><p className="muted">Customer record missing.</p></Card>;
  const start = () => {
    setForm({ firstName: c.firstName ?? "", lastName: c.lastName ?? "", email: c.email, phone: phone(c.phone), allowEmail: c.allowEmail, allowText: c.allowText });
    setEditing(true);
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <Card title="Customer Information" icon={User}
      actions={!editing && <button type="button" className="icon-link" aria-label="Edit customer" onClick={start}><Pencil size={18} /></button>}>
      <div className="cust-grid"><div>
      {editing ? (
        <form className="mini-form" onSubmit={async (e) => { e.preventDefault(); if (await save("PUT", "/customer", form)) setEditing(false); }}>
          <div className="two"><label>First name<input value={form.firstName} onChange={set("firstName")} autoFocus /></label>
            <label>Last name<input value={form.lastName} onChange={set("lastName")} /></label></div>
          <label>Email<input type="email" required value={form.email} onChange={set("email")} /></label>
          <label>Phone<input type="tel" value={form.phone} onChange={set("phone")} /></label>
          <div className="checks"><label><input type="checkbox" checked={form.allowEmail} onChange={set("allowEmail")} /> Email OK</label>
            <label><input type="checkbox" checked={form.allowText} onChange={set("allowText")} /> Texts OK</label></div>
          <p className="hint">Changes apply to all of this customer's tickets.</p>
          <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Save</button>
            <button type="button" className="btn outline" onClick={() => setEditing(false)}>Cancel</button></div>
        </form>
      ) : (
        <div className="stack">
          <div><div className="lbl">Full Name</div><div className="val-strong"><Link to={`/customers/${c.id}`} title="Customer's record: all tickets, addresses, switches">{[c.firstName, c.lastName].filter(Boolean).join(" ") || "(no name)"}</Link>
            {c.isWholesale && <span className="tag">Wholesale</span>}</div></div>
          <div><div className="lbl">Email</div><a className="val-strong" href={`mailto:${c.email}`}>{c.email}</a></div>
          <div><div className="lbl">Phone</div><div className="val-strong icon-line"><Phone size={15} />
            {c.phone ? <a href={`tel:${c.phone}`}>{phone(c.phone)}</a> : "—"}</div></div>
          {detail.otherTickets.length > 0 && (
            <details className="others"><summary>{detail.otherTickets.length} other ticket{detail.otherTickets.length === 1 ? "" : "s"}</summary>
              <ul>{detail.otherTickets.map((o) => <li key={o.ticketNo}><Link to={`/tickets/${o.ticketNo}`}>#{o.ticketNo}</Link> · {date(o.createdAt)} · {o.status}</li>)}</ul>
            </details>
          )}
        </div>
      )}
      </div>
      <AddressSection /></div>
    </Card>
  );
}

/** The ticket's shipping address (inside Customer Information). */
function AddressSection() {
  const { detail, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ street: "", street2: "", city: "", state: "", zip: "" });
  const [checkResult, setCheckResult] = useState<NonNullable<TicketDetail["addressCheck"]> | null>(null);
  const submit = async (body: Record<string, unknown>) => {
    const d = await save("PUT", "/address", body);
    if (!d) return;
    if (d.addressCheck) { setCheckResult(d.addressCheck); return; }
    setCheckResult(null);
    setEditing(false);
  };
  const line = (a: AddressFields) => [a.street, a.street2, `${a.city}, ${a.state} ${a.zip}`].filter(Boolean).join("\n");
  const start = () => { setForm({ street: t.street ?? "", street2: t.street2 ?? "", city: t.city ?? "", state: t.state ?? "", zip: t.zip ?? "" }); setEditing(true); };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="sub-section">
      <div className="sub-head"><span className="lbl"><MapPin size={14} /> Address</span>
        {!editing && <button type="button" className="icon-link" aria-label="Edit address" onClick={start}><Pencil size={16} /></button>}</div>
      {editing ? (
        checkResult ? (
          <div className="addr-check">
            <p className={checkResult.valid ? "hint" : "warn"}>{checkResult.valid ? "Shippo suggests a correction:" : "Shippo could not confirm this address."}
              {checkResult.messages.length > 0 && <> {checkResult.messages.join(" ")}</>}</p>
            <div className="two">
              <div><span className="lbl">You entered</span><pre>{line(checkResult.entered)}</pre></div>
              {checkResult.valid && <div><span className="lbl">Suggested</span><pre>{line(checkResult.suggestion)}</pre></div>}
            </div>
            <div className="btn-row">
              {checkResult.valid && <button type="button" className="btn primary" disabled={saving} onClick={() => void submit({ ...checkResult.suggestion, checked: true })}>Use suggested</button>}
              <button type="button" className="btn outline" disabled={saving} onClick={() => void submit({ ...checkResult.entered, checked: true })}>Keep what I entered</button>
              <button type="button" className="link-btn" onClick={() => setCheckResult(null)}>Edit again</button>
            </div>
          </div>
        ) :
        <form className="mini-form" onSubmit={async (e) => { e.preventDefault(); await submit(form); }}>
          <label>Street<input required value={form.street} onChange={set("street")} autoFocus /></label>
          <label>Street 2<input value={form.street2} onChange={set("street2")} /></label>
          <div className="three"><label>City<input required value={form.city} onChange={set("city")} /></label>
            <label>State<input required value={form.state} onChange={set("state")} /></label>
            <label>Zip<input required value={form.zip} onChange={set("zip")} /></label></div>
          <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>{saving ? "Checking…" : "Save"}</button>
            <button type="button" className="btn outline" onClick={() => setEditing(false)}>Cancel</button></div>
          <p className="hint">Checked with Shippo before saving.</p>
        </form>
      ) : t.street ? (
        <ul className="addr">
          <li><MapPin size={16} />{t.street}</li>
          {t.street2 && <li><Hash size={16} />{t.street2}</li>}
          {t.poBox && <li><Mail size={16} />PO Box {t.poBox}</li>}
          <li><MapPin size={16} />{t.city}</li>
          <li><MapPin size={16} />{t.state}</li>
          <li><Hash size={16} />{t.zip}</li>
        </ul>
      ) : <p className="muted">No address on this ticket.</p>}
    </div>
  );
}

/** Label 1: the return label the customer uses to send the watch in. */
export function ReturnLabelCard() {
  const { detail } = useTicketContext();
  const intake = [...detail.labels].filter((l) => l.toShop).sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
  const track = intake?.trackingNumber ? trackingUrl(intake.carrier, intake.trackingNumber) : null;
  return (
    <Card title="Return Label" icon={Truck}>
      <div className="btn-col">
        {intake?.labelUrl
          ? <a className="btn primary block" href={intake.labelUrl} target="_blank" rel="noreferrer"><Eye size={18} />View Label</a>
          : <button type="button" className="btn primary block" disabled><Eye size={18} />View Label</button>}
        {track
          ? <a className="btn outline block" href={track} target="_blank" rel="noreferrer"><Truck size={18} />Track Shipment</a>
          : <button type="button" className="btn outline block" disabled><Truck size={18} />Track Shipment</button>}
        <MailerButton />
      </div>
      {!intake && <p className="hint">No return label on this ticket.</p>}
    </Card>
  );
}

/** Assigned watchmakers, under Location in the summary (owner, 2026-11-03). */
function Watchmakers() {
  const { detail, lookups, save, saving } = useTicketContext();
  const [adding, setAdding] = useState(false);
  const assigned = new Set(detail.watchmakers.map((w) => w.id));
  const options = lookups?.locations.filter((l) => l.kind !== "shop" && l.isActive && !assigned.has(l.id)) ?? [];
  return (
    <span className="wm">
      {detail.watchmakers.map((w) => (
        <span key={w.id} className="chip blue" title={`Assigned ${date(w.assignedAt)}`}>{w.name}
          <button type="button" aria-label={`Remove ${w.name}`} disabled={saving}
            onClick={() => { void confirmBox(`Remove ${w.name} from this ticket?`).then((ok) => { if (ok) void save("DELETE", `/watchmakers/${w.id}`); }); }}>×</button>
        </span>
      ))}
      {adding ? (
        <select autoFocus defaultValue="" disabled={saving} aria-label="Assign a watchmaker" onBlur={() => setAdding(false)}
          onChange={(e) => { if (e.target.value) void save("POST", "/watchmakers", { vendorId: e.target.value }); setAdding(false); }}>
          <option value="">Choose…</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      ) : (
        <button type="button" className="icon-link" aria-label="Assign a watchmaker" title="Assign a watchmaker" onClick={() => setAdding(true)}><Plus size={16} /></button>
      )}
    </span>
  );
}

/** Mailer to Customer (draft, owner 2026-11-04): mail the customer their label, or a label and a box. */
function MailerButton() {
  const { detail, save, saving } = useTicketContext();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"label" | "box">("label");
  const [note, setNote] = useState("");
  const m = detail.mailer;
  return (<>
    <button type="button" className="btn outline block" disabled={saving || !!detail.ticket.checkedInAt}
      title={detail.ticket.checkedInAt ? "Already checked in" : "Mail the customer their label, or a label and a box"}
      onClick={() => { setKind(m?.kind ?? "label"); setNote(""); setOpen(true); }}><Mail size={18} />Mailer to Customer</button>
    {m && <p className="hint center">{m.state === "sent" ? `Mailer sent ${date(m.sentAt)}${m.trackingNumber ? ` · ${m.trackingNumber}` : ""}`
      : `Mailer requested ${date(m.requestedAt)} (${m.kind === "box" ? "label and box" : "label"})`} · <Link to="/leads?tab=todo">Leads</Link></p>}
    {open && (
      <Modal title="Mailer to Customer" onClose={() => setOpen(false)}>
        <form className="mini-form" onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/mailer", { kind, note })) setOpen(false); }}>
          <fieldset className="radios"><legend className="lbl">What should we mail?</legend>
            <label><input type="radio" checked={kind === "label"} onChange={() => setKind("label")} /> The return label in a stamped envelope → Mail Label</label>
            <label><input type="radio" checked={kind === "box"} onChange={() => setKind("box")} /> A label and a box → Mail Box</label>
          </fieldset>
          <label>Note (optional)<input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. customer called, no printer" /></label>
          <p className="hint">It goes in Leads → To do, where it's printed and sent (→ Mailer Outgoing).</p>
          <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Request mailer</button>
            <button type="button" className="btn outline" onClick={() => setOpen(false)}>Cancel</button></div>
        </form>
      </Modal>
    )}
  </>);
}
